/**
 * Transporte REST del frente, en UN solo sitio.
 *
 * Toda llamada cruda a nuestra API (las que no pasan por el cliente tipado
 * `api` de `lib/api.ts`) comparte DOS decisiones, y ninguna de las dos es del
 * módulo que llama:
 *
 *   1. **Cómo se autentica.** Web = cookie HttpOnly `space.device` con
 *      `credentials: "include"` + selector `X-Active-Account` (+
 *      `X-Impersonation-Token` si aplica). Tauri = NADA en JS: el Bearer lo
 *      inyecta el proxy Rust `api_request`, al que se llega vía
 *      {@link platformApiFetch}. Esa asimetría la resuelve `apiAuthHeaders()`
 *      (que devuelve `{}` en native) — copiarla a mano es justo lo que rompía
 *      el escritorio.
 *   2. **Cómo se convierte un error del servidor en un mensaje.** 401 =
 *      sesión caducada (accionable), si no el `message` del cuerpo JSON, si no
 *      `HTTP <status>`; y el código queda en `.status` para quien lo necesite.
 *
 * Existía escrito trece veces: nueve módulos de `features/` con su propio
 * `authHeaders()` + `fetch` pelado (que en Tauri NO llevaba credencial alguna,
 * porque ni pasaban por el proxy Rust ni podían: el token no vive en JS), y
 * cuatro con su propio `jsonFetch`. Las nueve copias, además, daban CINCO
 * mensajes distintos para el mismo 401.
 */
import { apiAuthHeaders, platformApiFetch, SERVER_BASE_URL } from "@/lib/api";

/** Origen de la API sin barra final. Los `path` de este módulo cuelgan de aquí. */
export const API_ORIGIN = SERVER_BASE_URL.replace(/\/$/, "");

/** Error de una respuesta no-ok, con el código HTTP accesible. */
export interface ApiRequestError extends Error {
  status: number;
}

/**
 * Mensaje de una respuesta no-ok. Consume el cuerpo, así que solo se llama
 * cuando ya se sabe que la respuesta falló.
 */
function withStatus(error: Error, status: number): ApiRequestError {
  return Object.assign(error, { status });
}

async function errorFrom(response: Response): Promise<ApiRequestError> {
  if (response.status === 401) {
    // Sesión caducada a mitad de acción: mensaje accionable, no "HTTP 401"
    // (el shell de auth redirige al login en la siguiente navegación).
    return withStatus(
      new Error("Sesión expirada: recarga la página e inicia sesión de nuevo"),
      response.status,
    );
  }
  let message = `HTTP ${response.status}`;
  try {
    const body: unknown = await response.json();
    if (
      body &&
      typeof body === "object" &&
      "message" in body &&
      typeof body.message === "string" &&
      body.message
    ) {
      ({ message } = body);
    }
  } catch {
    // sin cuerpo JSON: se queda el `HTTP <status>`
  }
  return withStatus(new Error(message), response.status);
}

/**
 * Lee el cuerpo JSON de una respuesta, lanzando {@link ApiRequestError} si no
 * es ok. Un 204 / cuerpo vacío resuelve a `undefined`.
 *
 * Público porque hay una superficie —el `ask` público del asistente— que a
 * propósito NO manda credenciales (es anónimo) pero sí comparte el trato de
 * errores; ese es el único caso legítimo de saltarse {@link apiFetch}.
 */
export async function readApiJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    throw await errorFrom(response);
  }
  const text = await response.text();
  // Única frontera sin tipar del cliente crudo: el servidor es la autoridad del shape.
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * `fetch` autenticado contra la API. `path` es relativo al origen de la API
 * (p.ej. `/api/crm/leads`). Devuelve la `Response` cruda para quien necesite
 * cabeceras o un cuerpo que no sea JSON.
 */
export function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  // Multipart: el `Content-Type` lo pone el navegador, porque solo él conoce el
  // `boundary` que el parser del servidor necesita. Fijarlo a mano rompe la subida.
  const isMultipart = typeof FormData !== "undefined" && init.body instanceof FormData;
  return platformApiFetch(`${API_ORIGIN}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(isMultipart ? {} : { "Content-Type": "application/json" }),
      ...apiAuthHeaders(),
      ...init.headers,
    },
  });
}

/** Llamada JSON autenticada: el camino por defecto de todo cliente REST. */
export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  return readApiJson<T>(await apiFetch(path, init));
}
